import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useCategories } from '../../hooks/useCategories';
import { Category } from '../../types/category';
import {
  TagIcon,
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ChevronDoubleRightIcon,
  FolderIcon,
  CubeIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { useLanguage } from 'contexts/LanguageContext';
import ConfirmDeleteModal from '../common/ConfirmDeleteModal';
import CategoryForm from './CategoryForm';

interface CategoryListProps {
  triggerNewForm?: boolean;
  onFormOpened?: () => void;
  displayMode?: 'table' | 'card';
}

const CategoryList: React.FC<CategoryListProps> = ({ triggerNewForm, onFormOpened, displayMode: externalDisplayMode }) => {
  const { mainCategories, loading, error, deleteCategory, updateCategoryProductCounts } = useCategories();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<Category | null>(null);
  const [internalDisplayMode, setInternalDisplayMode] = useState<'table' | 'card'>('card');
  const displayMode = externalDisplayMode ?? internalDisplayMode;
  const setDisplayMode = setInternalDisplayMode;
  const [isCountingProducts, setIsCountingProducts] = useState(false);
  const productsLoadedRef = useRef(false);
  const { t } = useLanguage();

  // External trigger to open form (from CommonPageHeader button)
  useEffect(() => {
    if (triggerNewForm) {
      setSelectedCategory(null);
      setIsModalOpen(true);
      onFormOpened?.();
    }
  }, [triggerNewForm, onFormOpened]);

  // Termékszámok lekérdezése függvény - újrafelhasználható
  const fetchProductCounts = useCallback(async () => {
    try {
      setIsCountingProducts(true);
      console.log('Termékszámok lekérdezése kezdődik');
      
      // Timeout a biztonság kedvéért, hogy garantáltan eltűnjön a loading állapot
      const countTimeout = setTimeout(() => {
        setIsCountingProducts(false);
        console.log('Kényszerített timeout: számolás befejeződött');
      }, 15000); // 15 másodperc után mindenképp befejezzük
      
      await updateCategoryProductCounts();
      clearTimeout(countTimeout);
      setIsCountingProducts(false);
      console.log('Termékszámok lekérdezése sikeres');
    } catch (error) {
      console.error('Hiba a termékszámok lekérdezésekor:', error);
      setIsCountingProducts(false);
    }
  }, [updateCategoryProductCounts]);

  // Load product counts for categories - egyszer fussuk le, amikor a loading befejeződik
  useEffect(() => {
    // Csak akkor futtatjuk, ha betöltődtek a kategóriák és még nem kértük le a termékszámokat
    if (!loading && !productsLoadedRef.current) {
      console.log('Kezdődik a termékszámok lekérdezése');
      productsLoadedRef.current = true; // Megjelöljük, hogy elindult a lekérdezés
      fetchProductCounts();
    }
  // Kizárólag a loading függőségre figyelünk, nem a updateCategoryProductCounts-ra
  }, [loading, fetchProductCounts]);

  const toggleExpand = (categoryId: string) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  // Minden kategória kinyitása
  const expandAll = () => {
    const allExpanded: Record<string, boolean> = {};
    mainCategories.forEach(category => {
      if (category.subcategories && category.subcategories.length > 0) {
        allExpanded[category.id] = true;
      }
    });
    setExpandedCategories(allExpanded);
  };

  // Minden kategória összecsukása
  const collapseAll = () => {
    setExpandedCategories({});
  };

  const handleDeleteConfirm = async () => {
    if (categoryToDelete) {
      try {
        await deleteCategory(categoryToDelete.id);
        setIsConfirmDeleteOpen(false);
        setCategoryToDelete(null);
      } catch (error) {
        console.error('Error deleting category:', error);
      }
    }
  };

  const handleAddClick = () => {
    setSelectedCategory(null);
    setIsModalOpen(true);
  };

  const handleEditClick = (category: Category) => {
    setSelectedCategory(category);
    setIsModalOpen(true);
  };

  const handleDeleteClick = (category: Category) => {
    setCategoryToDelete(category);
    setIsConfirmDeleteOpen(true);
  };

  // Manuális termékszám frissítés
  const handleRefreshProductCounts = () => {
    fetchProductCounts();
  };

  // Meghatározza, hogy a kategória törlése veszélyes-e (tartalmaz-e termékeket)
  const isDangerousDelete = (category: Category | null): boolean => {
    return Boolean(category?.productCount && category.productCount > 0);
  };

  // Rendereljük a kategória státusz badge-et
  const renderStatusBadge = (isActive: boolean) => (
    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium shadow-sm ${
      isActive 
        ? 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-400 border border-green-200 dark:border-green-700' 
        : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300 border border-gray-200 dark:border-gray-600'
      }`}
    >
      {isActive 
        ? <CheckCircleIcon className="w-3.5 h-3.5 mr-1.5 text-green-600 dark:text-green-400" /> 
        : <XCircleIcon className="w-3.5 h-3.5 mr-1.5 text-gray-500 dark:text-gray-400" />}
      {isActive ? t('common.active') : t('common.inactive')}
    </span>
  );

  // Rendereljük a termékszám badge-et
  const renderProductCountBadge = (count: number | undefined) => (
    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 border border-blue-200 dark:border-blue-700 shadow-sm">
      <CubeIcon className="w-3.5 h-3.5 mr-1.5" />
      {isCountingProducts ? (
        <svg className="animate-spin h-3 w-3 text-blue-500 dark:text-blue-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      ) : (
        count || 0
      )}
    </span>
  );

  // Kártyás nézet renderelése
  const renderCardView = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
      {mainCategories.map(category => (
        <div key={category.id} className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-lg transition-shadow duration-300 transform hover:-translate-y-1">
          {/* Kategória fejléc */}
          <div 
            className={`p-4 flex items-center justify-between border-b ${
              expandedCategories[category.id] 
                ? 'bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20 border-orange-200 dark:border-orange-600' 
                : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
            }`}
          >
            <div className="flex items-center space-x-3">
              <div className="p-2.5 rounded-lg bg-gradient-to-br from-orange-500/30 to-yellow-600/30 dark:from-orange-500/20 dark:to-yellow-600/20 border-orange-500/30 dark:border-orange-500/20 border shadow-sm">
                <FolderIcon className="h-5 w-5 text-orange-600 dark:text-orange-400" />
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200 flex items-center">
                  {category.name}
                </h3>
                <div className="flex items-center mt-1.5 space-x-2">
                  {renderStatusBadge(category.isActive)}
                  {renderProductCountBadge(category.productCount || 0)}
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-1.5">
              <button
                onClick={() => handleEditClick(category)}
                className="p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
              >
                <PencilSquareIcon className="h-4 w-4" />
              </button>
              <button
                onClick={() => handleDeleteClick(category)}
                className="p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
              >
                <TrashIcon className="h-4 w-4" />
              </button>
              {category.subcategories && category.subcategories.length > 0 && (
                <button
                  onClick={() => toggleExpand(category.id)}
                  className={`p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-all ${
                    expandedCategories[category.id] ? 'bg-orange-100 dark:bg-orange-900/20 text-orange-700 dark:text-orange-400' : ''
                  }`}
                >
                  {expandedCategories[category.id] ? (
                    <ChevronDownIcon className="h-4 w-4" />
                  ) : (
                    <ChevronRightIcon className="h-4 w-4" />
                  )}
                </button>
              )}
            </div>
          </div>
          
          {/* Alkategóriák */}
          {expandedCategories[category.id] && category.subcategories && category.subcategories.length > 0 && (
            <div className="bg-gray-50 dark:bg-gray-700 p-3">
              <div className="space-y-2.5">
                {category.subcategories.map(subCategory => (
                  <div 
                    key={subCategory.id} 
                    className="flex items-center justify-between p-2.5 rounded-lg bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-600 shadow-sm hover:shadow-md transition-all hover:border-gray-200 dark:hover:border-gray-500"
                  >
                    <div className="flex items-center space-x-2.5">
                      <div className="flex items-center">
                        <ChevronDoubleRightIcon className="h-3 w-3 text-gray-400 dark:text-gray-500 mr-1" />
                        <div className="p-1.5 rounded-lg bg-gradient-to-br from-yellow-500/20 to-amber-600/20 dark:from-yellow-500/10 dark:to-amber-600/10 border-yellow-500/20 dark:border-yellow-500/10 border shadow-sm">
                          <TagIcon className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
                        </div>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-800 dark:text-gray-200">{subCategory.name}</div>
                        <div className="flex items-center mt-1 space-x-2">
                          {renderStatusBadge(subCategory.isActive)}
                          {renderProductCountBadge(subCategory.productCount || 0)}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-1.5">
                      <button
                        onClick={() => handleEditClick(subCategory)}
                        className="p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                      >
                        <PencilSquareIcon className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => handleDeleteClick(subCategory)}
                        className="p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                      >
                        <TrashIcon className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );

  // Táblázatos nézet renderelése
  const renderTableView = () => (
    <div className="bg-white dark:bg-gray-800 shadow-md rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-5 py-3.5 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('settings.categories.fields.name')}</th>
              <th className="px-5 py-3.5 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('settings.categories.fields.productCount')}</th>
              <th className="px-5 py-3.5 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Státusz</th>
              <th className="px-5 py-3.5 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('settings.categories.fields.actions')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {mainCategories.map(category => (
              <React.Fragment key={category.id}>
                <tr className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                  <td className="px-5 py-3.5">
                    <div className="flex items-center">
                      <button
                        onClick={() => toggleExpand(category.id)}
                        className="mr-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                      >
                        {category.subcategories && category.subcategories.length > 0 ? (
                          expandedCategories[category.id] ? (
                            <ChevronDownIcon className="h-4 w-4" />
                          ) : (
                            <ChevronRightIcon className="h-4 w-4" />
                          )
                        ) : (
                          <div className="w-4"></div>
                        )}
                      </button>
                      <div className="p-1.5 rounded-lg bg-gradient-to-br from-orange-500/20 to-yellow-600/20 dark:from-orange-500/10 dark:to-yellow-600/10 border-orange-500/20 dark:border-orange-500/10 border shadow-sm mr-2.5">
                        <FolderIcon className="h-4 w-4 text-orange-600 dark:text-orange-400" />
                      </div>
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{category.name}</span>
                    </div>
                  </td>
                  <td className="px-5 py-3.5">
                    {renderProductCountBadge(category.productCount || 0)}
                  </td>
                  <td className="px-5 py-3.5 text-center">
                    {renderStatusBadge(category.isActive)}
                  </td>
                  <td className="px-5 py-3.5 text-right">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => handleEditClick(category)}
                        className="p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                      >
                        <PencilSquareIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteClick(category)}
                        className="p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
                {expandedCategories[category.id] &&
                  category.subcategories &&
                  category.subcategories.length > 0 &&
                  category.subcategories.map(subCategory => (
                    <tr key={subCategory.id} className="bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors">
                      <td className="px-5 py-3">
                        <div className="flex items-center pl-6">
                          <div className="flex items-center">
                            <ChevronDoubleRightIcon className="h-3 w-3 text-gray-400 dark:text-gray-500 mr-1.5" />
                            <div className="p-1 rounded-lg bg-gradient-to-br from-yellow-500/20 to-amber-600/20 dark:from-yellow-500/10 dark:to-amber-600/10 border-yellow-500/20 dark:border-yellow-500/10 border shadow-sm mr-2">
                              <TagIcon className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
                            </div>
                          </div>
                          <span className="text-sm font-medium text-gray-800 dark:text-gray-200">{subCategory.name}</span>
                        </div>
                      </td>
                      <td className="px-5 py-3">
                        {renderProductCountBadge(subCategory.productCount || 0)}
                      </td>
                      <td className="px-5 py-3 text-center">
                        {renderStatusBadge(subCategory.isActive)}
                      </td>
                      <td className="px-5 py-3 text-right">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => handleEditClick(subCategory)}
                            className="p-1.5 rounded-full hover:bg-white dark:hover:bg-gray-600 text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                          >
                            <PencilSquareIcon className="h-3.5 w-3.5" />
                          </button>
                          <button
                            onClick={() => handleDeleteClick(subCategory)}
                            className="p-1.5 rounded-full hover:bg-white dark:hover:bg-gray-600 text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                          >
                            <TrashIcon className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Kiemelkedő fejléc sáv */}
      <div className="bg-white dark:bg-gray-800 shadow-sm rounded-lg p-4 border border-gray-200 dark:border-gray-700">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div className="flex items-center gap-3">
            {/* Eltávolítva a felirat */}
            
            {/* Kibontás/összecsukás és termékszám frissítés gombok */}
            {!loading && mainCategories.length > 0 && (
              <div className="flex items-center space-x-2">
                <button
                  onClick={expandAll}
                  className="inline-flex items-center px-3 py-2 text-xs font-medium rounded-md bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-gray-800"
                  title="Minden kategória kibontása"
                >
                  <ChevronDownIcon className="w-3.5 h-3.5 mr-1.5 text-gray-600 dark:text-gray-400" />
                  <span>Összes kibontása</span>
                </button>
                <button
                  onClick={collapseAll}
                  className="inline-flex items-center px-3 py-2 text-xs font-medium rounded-md bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-gray-800"
                  title="Minden kategória összecsukása"
                >
                  <ChevronRightIcon className="w-3.5 h-3.5 mr-1.5 text-gray-600 dark:text-gray-400" />
                  <span>Összes összecsukása</span>
                </button>
                <button
                  onClick={handleRefreshProductCounts}
                  disabled={isCountingProducts}
                  className={`inline-flex items-center px-3 py-2 text-xs font-medium rounded-md bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-gray-800 ${isCountingProducts ? 'opacity-50 cursor-not-allowed' : ''}`}
                  title="Termékszámok frissítése"
                >
                  <ArrowPathIcon className={`w-3.5 h-3.5 mr-1.5 text-gray-600 dark:text-gray-400 ${isCountingProducts ? 'animate-spin' : ''}`} />
                  <span>Termékszámok frissítése</span>
                </button>
              </div>
            )}
          </div>
          
        </div>
      </div>

      {loading ? (
        <div className="bg-white dark:bg-gray-800 shadow-sm rounded-lg p-8">
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-600"></div>
          </div>
        </div>
      ) : error ? (
        <div className="bg-white dark:bg-gray-800 shadow-sm rounded-lg p-4">
          <div className="bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 p-4 rounded-lg">
            {t('common.errors.loadingFailed')}
          </div>
        </div>
      ) : mainCategories.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 shadow-sm rounded-lg p-6 text-center">
          <TagIcon className="h-12 w-12 mx-auto text-gray-400 dark:text-gray-500" />
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">{t('settings.categories.noCategories')}</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{t('settings.categories.noCategoriesTooltip')}</p>
          <div className="mt-6">
            <button
              type="button"
              onClick={handleAddClick}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-gray-800"
            >
              <PlusIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
              {t('settings.categories.add')}
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 shadow-sm rounded-lg p-4 border border-gray-200 dark:border-gray-700">
          {displayMode === 'card' ? renderCardView() : renderTableView()}
        </div>
      )}

      <CategoryForm
        isOpen={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          setSelectedCategory(null);
        }}
        initialData={selectedCategory || undefined}
      />

      <ConfirmDeleteModal
        isOpen={isConfirmDeleteOpen}
        onClose={() => {
          setIsConfirmDeleteOpen(false);
          setCategoryToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title={t('settings.categories.messages.confirmTitle')}
        deleteMessage={categoryToDelete ? 
          `${t('settings.categories.messages.deleteQuestion')} "${categoryToDelete.name}"?` : 
          t('settings.categories.messages.deleteQuestion')}
        deleteWarning={
          isDangerousDelete(categoryToDelete) ? 
            t('settings.categories.messages.dangerousDeleteWarning') : 
            t('settings.categories.messages.deleteWarning')
        }
        danger={isDangerousDelete(categoryToDelete)}
      />
    </div>
  );
};

export default CategoryList; 